/**
 * Catalogo de eventos de dominio — **contrato do produto**, nao configuracao de instancia.
 *
 * ADR-0005 (revisao da virada de produto, ADR-0019) e categorico: *"os 8 nomes, o envelope, a
 * convencao sem sufixo de versao e o campo `phi` sao iguais em todas as instancias. Nenhuma
 * instancia acrescenta, remove ou renomeia evento."* Por isso o catalogo mora aqui, em
 * `@farmacia/contracts`, e nao em `apps/api`: um evento novo e PR no `CONTRACTS §4.1` para
 * todos os clientes.
 *
 * Fontes: `CONTRACTS §4` (envelope e os 8 do MVP), `DOMAIN §6` (a interface `DomainEvent`),
 * `ADR-0005` itens 4 e 5, `ARCHITECTURE §6.2` e `§6.3`.
 *
 * ## As tres regras que este arquivo torna executaveis
 *
 * 1. **Nome canonico `contexto.Agregado.FatoNoPassado`, SEM sufixo de versao** (ADR-0005 item
 *    5). `version` e campo do envelope. `EVENT_NAME_PATTERN` recusa `orders.Order.Placed.v2`.
 * 2. **Payload sem PII e sem PHI: so identificadores** (ADR-0005 item 4). Quem precisa do dado
 *    busca no modulo dono, sob autorizacao. E isso que mantem log, fila, DLQ e backup livres de
 *    dado de saude. O gate esta em `events.test.ts`, que varre as chaves de cada payload contra
 *    uma lista de campos proibidos — e falha quando alguem acrescenta `customerName`.
 * 3. **Um evento so existe se tiver consumidor que nao pode ser chamado direto**
 *    (`ARCHITECTURE §6.1`). Por isso `MVP_EVENT_NAMES` tem 8 nomes e nao 9: os demais sao
 *    reserva de nomes em `CONTRACTS §4.2` e **nao ganham schema aqui**.
 *
 * ## O que NAO esta aqui, e por que
 *
 * O **produtor**. Um schema de payload nao emite nada; quem emite e o caso de uso do modulo
 * dono, gravando na mesma transacao do agregado (`platform/outbox`). Ter o nome declarado e
 * nao ter produtor e o estado correto de um evento cujo modulo ainda nao existe — e e o estado
 * de todos os 8 hoje, porque `modules/orders`, `modules/payments` e `modules/inventory` ainda
 * nao foram escritos.
 */
import { z } from 'zod';

import { InstantSchema, NonNegativeCentsSchema, Uuidv7Schema } from './common';
import {
  ActorKindSchema,
  CancellationReasonSchema,
  FulfillmentModeSchema,
  OrderChannelSchema,
} from './enums';

// ---------------------------------------------------------------------------
// 1. Nomes
// ---------------------------------------------------------------------------

/**
 * `contexto.Agregado.FatoNoPassado` — tres segmentos, o do meio e o agregado em PascalCase e o
 * ultimo e um particípio.
 *
 * O padrao existe para recusar **uma** coisa em especial: o sufixo de versao. `.v2` no nome foi
 * a convencao anterior deste ADR e foi revogada (ADR-0005 item 5, D4). Num monolito com
 * consumidor no mesmo deploy, mudanca de payload e migration — nao um segundo nome de evento
 * que dobra o numero de handlers e de assinaturas de fila para sempre.
 */
export const EVENT_NAME_PATTERN = /^[a-z][a-z0-9]*\.[A-Z][A-Za-z0-9]*\.[A-Z][A-Za-z0-9]*$/;

/**
 * Os **8** eventos do MVP (`CONTRACTS §4.1`). Tupla `as const`: o tipo `MvpEventName` e a uniao
 * exata, e um nome fora dela nao compila.
 */
export const MVP_EVENT_NAMES = [
  'orders.Order.Placed',
  'orders.Order.Accepted',
  'orders.Order.Ready',
  'orders.Order.Cancelled',
  'payments.Charge.Paid',
  'payments.Charge.Expired',
  'orders.Order.PriceDivergenceDetected',
  'inventory.StoreFeed.Stale',
] as const;

export const MvpEventNameSchema = z.enum(MVP_EVENT_NAMES);
export type MvpEventName = z.infer<typeof MvpEventNameSchema>;

// ---------------------------------------------------------------------------
// 2. Payloads — SO identificadores (ADR-0005 item 4)
// ---------------------------------------------------------------------------

/**
 * `orders.Order.Placed` — o fato fundador (`DOMAIN §6`, BC-08).
 *
 * `hasRx` e o unico campo do catalogo inteiro que carrega **inferencia de saude**: "esta pessoa
 * comprou algo que exige receita". Ele esta no contrato (`CONTRACTS §4.1`) porque o consumidor
 * de notificacao precisa saber que NAO pode usar template livre — e e exatamente por isso que
 * o envelope deste evento nasce com `phi: true` quando `hasRx` e verdadeiro (ver `phiFor`).
 *
 * O que NAO entra: `customerId` esta aqui como identificador pseudonimo (e o que o consumidor
 * usa para pedir o dado ao modulo dono, sob autorizacao), mas nome, telefone, CPF, endereco e
 * **qualquer identificador de produto** ficam de fora. Um `productId` de medicamento e
 * inferencia de saude tanto quanto o nome do remedio (REG-044 / RN-123).
 */
export const OrderPlacedPayloadSchema = z
  .object({
    orderId: Uuidv7Schema,
    storeId: Uuidv7Schema,
    customerId: Uuidv7Schema,
    channel: OrderChannelSchema,
    mode: FulfillmentModeSchema,
    totalCents: NonNegativeCentsSchema,
    hasRx: z.boolean(),
  })
  .strict();
export type OrderPlacedPayload = z.infer<typeof OrderPlacedPayloadSchema>;

/** `orders.Order.Accepted` — marco de compromisso: libera separacao e lanca no ERP. */
export const OrderAcceptedPayloadSchema = z
  .object({
    orderId: Uuidv7Schema,
    storeId: Uuidv7Schema,
    /** `staff_user.id` de quem aceitou — pseudonimo interno, nunca nome (SECURITY §7.10). */
    acceptedBy: Uuidv7Schema,
    at: InstantSchema,
  })
  .strict();
export type OrderAcceptedPayload = z.infer<typeof OrderAcceptedPayloadSchema>;

/**
 * `orders.Order.Ready` — notificacao e contagem do prazo de retirada (4 h).
 *
 * `pickupCode` e opcional porque so existe em `RETIRADA`. Ele **nao** e credencial: e o codigo
 * curto que o balconista confere no balcao, e ja aparece na tela do cliente.
 */
export const OrderReadyPayloadSchema = z
  .object({
    orderId: Uuidv7Schema,
    storeId: Uuidv7Schema,
    mode: FulfillmentModeSchema,
    pickupCode: z.string().min(4).max(12).optional(),
  })
  .strict();
export type OrderReadyPayload = z.infer<typeof OrderReadyPayloadSchema>;

/** `orders.Order.Cancelled` — liberar reserva, disparar estorno, notificar. */
export const OrderCancelledPayloadSchema = z
  .object({
    orderId: Uuidv7Schema,
    storeId: Uuidv7Schema,
    reason: CancellationReasonSchema,
    /** Quem cancelou, por ESPECIE de ator. O id de quem cancelou ja vai no envelope (`actor`). */
    by: ActorKindSchema,
  })
  .strict();
export type OrderCancelledPayload = z.infer<typeof OrderCancelledPayloadSchema>;

/**
 * `payments.Charge.Paid` — avanco do pedido **so apos reconsulta ao PSP** (`CONTRACTS §4.1`).
 *
 * O evento nao afirma que o dinheiro entrou; afirma que o PSP disse que entrou. Quem consome
 * reconsulta antes de mover o pedido. O `txid` e o identificador do PSP, nao um valor de conta.
 */
export const ChargePaidPayloadSchema = z
  .object({
    chargeId: Uuidv7Schema,
    orderId: Uuidv7Schema,
    storeId: Uuidv7Schema,
    txid: z.string().min(1).max(64),
    amountCents: NonNegativeCentsSchema,
  })
  .strict();
export type ChargePaidPayload = z.infer<typeof ChargePaidPayloadSchema>;

/** `payments.Charge.Expired` — cancelar com `reason='PIX_EXPIRADO'` e liberar reserva. */
export const ChargeExpiredPayloadSchema = z
  .object({
    chargeId: Uuidv7Schema,
    orderId: Uuidv7Schema,
    storeId: Uuidv7Schema,
  })
  .strict();
export type ChargeExpiredPayload = z.infer<typeof ChargeExpiredPayloadSchema>;

/**
 * `orders.Order.PriceDivergenceDetected` — o incidente P1 de confianca (auditoria 03 §1.4).
 *
 * Os dois valores sao dinheiro, e dinheiro **nao e PII**: sao o snapshot que o app mostrou e o
 * preco que o ERP cobrou. Sem os dois no payload o alerta obrigaria o consumidor a reabrir o
 * pedido so para saber se a divergencia passa do limiar (`CONTRACTS §10`).
 */
export const PriceDivergenceDetectedPayloadSchema = z
  .object({
    orderId: Uuidv7Schema,
    storeId: Uuidv7Schema,
    appSnapshotCents: NonNegativeCentsSchema,
    erpPriceCents: NonNegativeCentsSchema,
  })
  .strict();
export type PriceDivergenceDetectedPayload = z.infer<typeof PriceDivergenceDetectedPayloadSchema>;

/** `inventory.StoreFeed.Stale` — degradar a filial para `ON_REQUEST` antes de vender ar (A2). */
export const StoreFeedStalePayloadSchema = z
  .object({
    storeId: Uuidv7Schema,
    syncedAt: InstantSchema,
  })
  .strict();
export type StoreFeedStalePayload = z.infer<typeof StoreFeedStalePayloadSchema>;

// ---------------------------------------------------------------------------
// 3. O catalogo, indexado por nome
// ---------------------------------------------------------------------------

/**
 * Nome -> schema do payload. **Fechado**: `Record<MvpEventName, ...>` obriga o compilador a
 * cobrar uma entrada por nome, entao acrescentar um nome a `MVP_EVENT_NAMES` sem escrever o
 * payload nao compila.
 */
export const EVENT_PAYLOAD_SCHEMAS = {
  'orders.Order.Placed': OrderPlacedPayloadSchema,
  'orders.Order.Accepted': OrderAcceptedPayloadSchema,
  'orders.Order.Ready': OrderReadyPayloadSchema,
  'orders.Order.Cancelled': OrderCancelledPayloadSchema,
  'payments.Charge.Paid': ChargePaidPayloadSchema,
  'payments.Charge.Expired': ChargeExpiredPayloadSchema,
  'orders.Order.PriceDivergenceDetected': PriceDivergenceDetectedPayloadSchema,
  'inventory.StoreFeed.Stale': StoreFeedStalePayloadSchema,
} as const satisfies Record<MvpEventName, z.ZodTypeAny>;

/** O tipo do payload de cada nome, derivado do catalogo — nunca redigitado. */
export type EventPayloadOf<TName extends MvpEventName> = z.infer<
  (typeof EVENT_PAYLOAD_SCHEMAS)[TName]
>;

/**
 * O payload como o **produtor** o escreve, antes do `parse`.
 *
 * A diferenca em relacao a `EventPayloadOf` e uma so, e ela nao e cosmetica: `CentsSchema`
 * transforma `number` na marca `Cents` (`common.ts`), e a marca so existe DEPOIS da validacao.
 * Exigir `Cents` na entrada obrigaria todo produtor a converter com um cast — e um cast e
 * exatamente o que o tipo marcado existe para impedir. Entao a porta recebe a forma crua,
 * valida, e devolve o valor marcado.
 */
export type EventPayloadInputOf<TName extends MvpEventName> = z.input<
  (typeof EVENT_PAYLOAD_SCHEMAS)[TName]
>;

/**
 * O tipo do agregado que cada evento carrega (`aggregate.type` do envelope, `DOMAIN §6`).
 *
 * E o **valor** que vai para `outbox_event.aggregateType`, e o comentario do schema Prisma
 * ja fixa o vocabulario: `"order" | "payment" | "inventory"`.
 */
export const EVENT_AGGREGATE_TYPES = {
  'orders.Order.Placed': 'order',
  'orders.Order.Accepted': 'order',
  'orders.Order.Ready': 'order',
  'orders.Order.Cancelled': 'order',
  'payments.Charge.Paid': 'payment',
  'payments.Charge.Expired': 'payment',
  'orders.Order.PriceDivergenceDetected': 'order',
  'inventory.StoreFeed.Stale': 'inventory',
} as const satisfies Record<MvpEventName, 'order' | 'payment' | 'inventory'>;

export type EventAggregateType = (typeof EVENT_AGGREGATE_TYPES)[MvpEventName];

/**
 * **A regra de `phi`, escrita uma vez.** ADR-0005 item 4 e `DATA_MODEL`: *"true quando o payload
 * pode conter inferencia de saude — barra analytics e templates (RN-123)"*.
 *
 * Hoje ha exatamente um caso no catalogo: `orders.Order.Placed` com `hasRx: true`. Deixar isso
 * como decisao de cada produtor seria garantir que o proximo produtor esqueca — e um evento com
 * `phi: false` indevido entra em analytics e em template livre sem que nada reclame. Por isso a
 * funcao e do CONTRATO, e o gate de PHI (`test:phi-leak`) tem um so lugar para conferir.
 */
export function phiFor(name: MvpEventName, payload: unknown): boolean {
  if (name !== 'orders.Order.Placed') return false;

  // O produtor pode estar montando o payload; `hasRx` ausente e tratado como PHI. Errar para o
  // lado de `true` custa um evento fora de analytics; errar para `false` vaza inferencia de
  // saude, e essa assimetria decide o default.
  const hasRx = (payload as Partial<OrderPlacedPayload> | null)?.hasRx;
  return hasRx !== false;
}

// ---------------------------------------------------------------------------
// 4. Envelope
// ---------------------------------------------------------------------------

/**
 * O ator do envelope (`DOMAIN §6`). `id` e nulo para `SYSTEM` — o job de expiracao de PIX nao
 * tem gente atras dele, e inventar um id de servico ali criaria um "usuario" que a trilha de
 * auditoria trataria como pessoa.
 */
export const EventActorSchema = z
  .object({
    kind: ActorKindSchema,
    id: Uuidv7Schema.nullable(),
  })
  .strict();
export type EventActor = z.infer<typeof EventActorSchema>;

/**
 * O envelope canonico, igual em toda instancia (`CONTRACTS §4`, `DOMAIN §6`).
 *
 * `storeId` e **nulavel** aqui e no schema Prisma, apesar de `DOMAIN §6` tipa-lo como `StoreId`:
 * ha evento de dominio sem dimensao de filial (o comentario de `outbox_event.storeSeq` diz
 * "nulo quando `storeId` e nulo — esses eventos nunca aparecem no feed do Painel"). Os 8 do MVP
 * todos tem filial; a nulabilidade e do MECANISMO, nao do catalogo.
 */
export const EventEnvelopeSchema = z
  .object({
    eventId: Uuidv7Schema,
    name: MvpEventNameSchema,
    version: z.literal(1),
    occurredAt: InstantSchema,
    aggregate: z
      .object({ type: z.enum(['order', 'payment', 'inventory']), id: Uuidv7Schema })
      .strict(),
    actor: EventActorSchema,
    storeId: Uuidv7Schema.nullable(),
    correlationId: z.string().uuid(),
    causationId: z.string().uuid().optional(),
    phi: z.boolean(),
    payload: z.unknown(),
  })
  .strict();
export type EventEnvelope = z.infer<typeof EventEnvelopeSchema>;

/**
 * Envelope **com o payload ja tipado pelo nome**. E o que o produtor monta e o que o consumidor
 * recebe; a versao com `payload: unknown` acima e so a forma no fio.
 */
export interface DomainEvent<TName extends MvpEventName = MvpEventName> {
  readonly eventId: string;
  readonly name: TName;
  readonly version: 1;
  readonly occurredAt: Date;
  readonly aggregate: { readonly type: EventAggregateType; readonly id: string };
  readonly actor: EventActor;
  readonly storeId: string | null;
  readonly correlationId: string;
  readonly causationId?: string | undefined;
  readonly phi: boolean;
  readonly payload: EventPayloadOf<TName>;
}

/**
 * Valida um envelope contra o catalogo: nome conhecido, payload conforme o schema **daquele**
 * nome e `phi` coerente com `phiFor`.
 *
 * Um envelope que chega com `phi: false` num `Placed` com `hasRx: true` e recusado, e nao
 * corrigido em silencio: corrigir esconderia o produtor defeituoso, e o proximo evento dele
 * poderia ser o que vaza.
 */
export function parseDomainEvent(input: unknown): DomainEvent {
  const envelope = EventEnvelopeSchema.parse(input);
  const payload = EVENT_PAYLOAD_SCHEMAS[envelope.name].parse(envelope.payload);

  const esperado = phiFor(envelope.name, payload);
  if (envelope.phi !== esperado) {
    throw new Error(
      `Envelope de '${envelope.name}' com phi=${String(envelope.phi)}; o catalogo exige ` +
        `phi=${String(esperado)} (ADR-0005 item 4, RN-123).`,
    );
  }

  if (EVENT_AGGREGATE_TYPES[envelope.name] !== envelope.aggregate.type) {
    throw new Error(
      `Envelope de '${envelope.name}' com aggregate.type='${envelope.aggregate.type}'; ` +
        `o catalogo exige '${EVENT_AGGREGATE_TYPES[envelope.name]}'.`,
    );
  }

  return {
    ...envelope,
    occurredAt: new Date(envelope.occurredAt),
    payload,
  };
}
